import { React, useEffect, useState, useCallback } from 'react';
import { Stack, Form, Table, Image, Pagination, Spinner } from 'react-bootstrap';
import * as api from 'services/api';
import _ from 'lodash';
import './AuditsList.css';
import { useTranslation } from 'react-i18next';
export function AuditsList() {
    const { t } = useTranslation();
    const [audits, setAudits] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        auditsPerPage: 10
    });
    const [sortParams, setSortParams] = useState({
        field: null,
        direction: 'desc'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const getAudits = useCallback(async (page, limit) => {
        const offset = (page - 1) * limit;
        const response = await api.getAudits(limit, offset, sortParams.field, sortParams.direction);

        if (response.status === 200) {
            setAudits(response.data.audits);
            setPagination(prevPagination => ({
                ...prevPagination,
                totalPages: Math.ceil(response.data.totalCount / limit)
            }));
        } else {
            console.error('Error al obtener el listado de usuarios');
        }
    }, [setAudits, setPagination, sortParams]);

    useEffect(() => {
        getAudits(pagination.currentPage, pagination.auditsPerPage);
    }, [pagination.currentPage, pagination.auditsPerPage, sortParams, getAudits]);

    // Función para formatear la fecha
    const formatLastConnection = (lastConnection) => {
        const date = new Date(lastConnection);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        const hours = date.getHours();
        const minutes = date.getMinutes();

        // Añadir ceros a la izquierda si es necesario
        const formattedDay = day < 10 ? `0${day}` : day;
        const formattedMonth = month < 10 ? `0${month}` : month;
        const formattedHours = hours < 10 ? `0${hours}` : hours;
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

        return `${formattedDay}/${formattedMonth}/${year} ${formattedHours}:${formattedMinutes}`;
    };

    // Función para cambiar de página
    const handlePageChange = (page) => {
        setPagination(prevPagination => ({
            ...prevPagination,
            currentPage: page
        }));

        getAudits(page, pagination.auditsPerPage);
    };

    // Función para cambiar el número de elementos por página
    const handlePerPageChange = (e) => {
        const perPage = parseInt(e.target.value);
        setPagination(prevPagination => ({
            ...prevPagination,
            currentPage: 1,
            auditsPerPage: perPage
        }));
    };

    // Función para ordenar la tabla
    const handleSort = (field) => {
        if (sortParams.field === field) {
            setSortParams(prevSortParams => ({
                ...prevSortParams,
                direction: prevSortParams.direction === "asc" ? "desc" : "asc"
            }));
        } else {
            setSortParams({ field, direction: "asc" });
        }
    };

    // Realizar la búsqueda en la tabla cada segundo 
    // después de que el usuario deje de escribir (debounce)
    const handleSearch = _.debounce(async (event) => {
        setLoading(true);
        const limit = pagination.auditsPerPage;
        const offset = 0;
        const searchText = event.target.value;

        await new Promise((resolve) => setTimeout(resolve, 300));
        const response = await api.getAudits(limit, offset, sortParams.field, sortParams.direction, searchText);

        setLoading(false);

        if (response.status === 200) {
            setAudits(response.data.audits);
            setPagination(prevPagination => ({
                ...prevPagination,
                totalPages: Math.ceil(response.data.totalCount / pagination.auditsPerPage)
            }));
        } else {
            setError(t('noResultsFound'));
        }
    }, 1000);

    return (
        <div className="AuditsList">

            <Form>
                <Stack direction="horizontal" gap="3" className="mt-4 justify-content-between">
                    <div className="AuditsList__searcherContainer">
                        <Form.Control
                            type="text"
                            placeholder={t('auDateUserAction')}
                            className="AuditsList__searcher bg-light"
                            onChange={handleSearch}
                        />
                        {loading && (
                            <Spinner animation="border" variant="secondary" size="sm" className="AuditsList__spinner" />
                        )}
                    </div>
                </Stack>
                <Stack direction="horizontal" className="justify-content-start">
                    {error && <p className="text-danger fw-semibold">{error}</p>}
                </Stack>
            </Form>

            {audits.length > 0 && (
                <div className="card rounded-3 overflow-auto p-2 mt-5">
                    <table hover className='table table-hover overflow-auto'>
                        <thead>
                            <tr >
                                <th onClick={() => handleSort('Id')}>{t('auTableId')}</th>
                                <th onClick={() => handleSort('Date')}>{t('auTableDate')}</th>
                                <th onClick={() => handleSort('User')}>{t('auTableUser')}</th>
                                <th onClick={() => handleSort('IpAddress')}>{t('auTableIp')}</th>
                                <th onClick={() => handleSort('Niu')}>{t('auTableNiu')}</th>
                                <th onClick={() => handleSort('ActionType')}>{t('auTableAction')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {audits.map((audit) => (
                                <tr key={audit.id}>
                                    <td className="align-middle">{audit.id}</td>
                                    <td className="align-middle">
                                        <Image src="./icons/calendar.svg" className="AuditsList__image" />
                                        {formatLastConnection(audit.date)}
                                    </td>
                                    <td className="align-middle">
                                        <Image src="./icons/user.svg" className="AuditsList__image" />
                                        {audit.user}
                                    </td>
                                    <td className="align-middle">
                                        <Image src="./icons/ip.svg" className="AuditsList__image" />
                                        {audit.ipAddress ? audit.ipAddress : <span>-</span>}
                                    </td>
                                    <td className="align-middle">
                                        <Image src="./icons/niu.svg" className="AuditsList__image" />
                                        {audit.niu ? audit.niu : <span>-</span>}
                                    </td>
                                    <td className="align-middle">
                                        <div><b>{audit.actionType}</b></div>
                                        <div>{audit.action}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination size="sm" className="AuditsList__pagination">
                        <div className="AuditsList__paginationText">{t('auRowsPerPage')}</div>
                        <Form.Select aria-label="Filas por página" size="sm" className="AuditsList__paginationSelect" value={pagination.auditsPerPage} onChange={handlePerPageChange}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={40}>40</option>
                            <option value={50}>50</option>
                        </Form.Select>
                        <div className="AuditsList__paginationText">{t('pPage')} {pagination.currentPage} {t('pFrom')} {pagination.totalPages}</div>
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} />
                        <Pagination.Next onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages} />
                    </Pagination>
                </div>
            )}
        </div>
    );
}